/**
 * Intelligent Script Loader
 * Defers loading of non-critical third-party scripts until:
 * 1. Browser is idle (requestIdleCallback)
 * 2. User interaction detected
 * 3. Timeout threshold reached
 * 
 * This reduces Total Blocking Time and improves Time to Interactive
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    idleTimeout: 3000,        // Load after 3s if idle
    interactionTimeout: 5000, // Load after 5s regardless
    interactionEvents: ['mousedown', 'touchstart', 'keydown', 'wheel', 'scroll']
  };

  // Track loading state
  let scriptsLoaded = false;
  let interactionDetected = false;

  /**
   * Scripts to defer - Add your third-party scripts here
   * Priority: low = load on idle, medium = load on interaction, high = load sooner
   */
  const deferredScripts = [
    {
      id: 'shopify-wpm',
      priority: 'medium',
      test: () => !window.Shopify?.analytics?.replayQueue,
      load: () => {
        // Web Pixel Manager - defer until needed
        const script = document.createElement('script');
        script.src = '/cdn/wpm/b/shopify_wpm.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    },
    {
      id: 'analytics-trekkie',
      priority: 'low',
      test: () => !window.Trekkie,
      load: () => {
        // Shopify Trekkie analytics - low priority
        const script = document.createElement('script');
        script.src = '/cdn/s/trekkie.storefront.min.js';
        script.async = true;
        document.body.appendChild(script);
      }
    },
    {
      id: 'trustify-reviews',
      priority: 'low',
      test: () => !document.querySelector('[data-trustify-loaded]'),
      load: () => {
        // Reviews widget - load on scroll near reviews section
        const reviewsSection = document.querySelector('[data-reviews-widget]');
        if (reviewsSection) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                loadTrustifyScript();
                observer.disconnect();
              }
            });
          }, { rootMargin: '200px' });
          observer.observe(reviewsSection);
        }
      }
    }
  ];

  /**
   * Load script with priority
   */
  function loadScript(scriptConfig) {
    if (scriptConfig.test && !scriptConfig.test()) {
      return; // Already loaded
    }
    
    try {
      scriptConfig.load();
      console.log(`[Script Loader] Loaded: ${scriptConfig.id}`);
    } catch (error) {
      console.error(`[Script Loader] Error loading ${scriptConfig.id}:`, error);
    }
  }

  /**
   * Load scripts by priority
   */
  function loadScriptsByPriority(priority) {
    deferredScripts
      .filter(script => script.priority === priority)
      .forEach(loadScript);
  }

  /**
   * Load all remaining scripts
   */
  function loadAllScripts() {
    if (scriptsLoaded) return;
    scriptsLoaded = true;

    deferredScripts.forEach(loadScript);
  }

  /**
   * Handle user interaction
   */
  function onInteraction() {
    if (interactionDetected) return;
    interactionDetected = true;

    // Load medium priority scripts on first interaction
    loadScriptsByPriority('medium');

    // Clean up event listeners
    CONFIG.interactionEvents.forEach(event => {
      document.removeEventListener(event, onInteraction, { passive: true });
    });
  }

  /**
   * Use requestIdleCallback if available, otherwise setTimeout
   */
  function scheduleIdleLoad(callback, timeout) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout });
    } else {
      setTimeout(callback, timeout);
    }
  }

  /**
   * Initialize the loader
   */
  function init() {
    // 1. Load low priority scripts when browser is idle
    scheduleIdleLoad(() => {
      loadScriptsByPriority('low');
    }, CONFIG.idleTimeout);

    // 2. Load medium priority scripts on user interaction
    CONFIG.interactionEvents.forEach(event => {
      document.addEventListener(event, onInteraction, { 
        once: true, 
        passive: true 
      });
    });

    // 3. Load all remaining scripts after timeout
    setTimeout(() => {
      loadAllScripts();
    }, CONFIG.interactionTimeout);

    // 4. Load high priority scripts immediately
    loadScriptsByPriority('high');
  }

  /**
   * Helper: Load Trustify script
   */
  function loadTrustifyScript() {
    const script = document.createElement('script');
    script.src = 'https://cdn.shopify.com/extensions/.../trustify-reviews.js';
    script.async = true;
    script.setAttribute('data-trustify-loaded', 'true');
    document.body.appendChild(script);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
